/**
 * Redesign v2 feature flag — persisted in localStorage so each admin
 * keeps their preference across sessions on the same browser.
 *
 * v2 is now the default: the flag is opt-out, so v2 is enabled unless
 * the admin has explicitly switched back to v1 (stored value "0").
 *
 * UI: the toggle row inside the V2 TopBar's account dropdown
 * ("Use v2 redesign", lives in TopBar.jsx). Components elsewhere
 * use the `useRedesignFlag` hook to read a reactive boolean.
 *
 * Storage key is intentionally namespaced ("tts.redesign.v2") so we
 * can add more flags later without colliding.
 */
import { useEffect, useState } from 'react';

const KEY = 'tts.redesign.v2';
const EVENT = 'tts:redesign-flag-changed';

export function isRedesignEnabled() {
  // Default-on: only an explicit opt-out ("0") disables v2.
  try { return localStorage.getItem(KEY) !== '0'; }
  catch (e) { return true; }
}

export function setRedesignEnabled(on) {
  try {
    localStorage.setItem(KEY, on ? '1' : '0');
    // Notify same-tab subscribers. The "storage" event only fires across
    // tabs, so we emit a custom event for in-page reactivity.
    window.dispatchEvent(new CustomEvent(EVENT, { detail: on }));
  } catch (e) { /* private browsing, etc. — fail silently */ }
}

export function useRedesignFlag() {
  const [on, setOn] = useState(isRedesignEnabled());
  useEffect(() => {
    const sync = () => setOn(isRedesignEnabled());
    window.addEventListener('storage', sync);            // cross-tab
    window.addEventListener(EVENT, sync);                // same-tab
    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener(EVENT, sync);
    };
  }, []);
  return [on, (v) => setRedesignEnabled(v)];
}
